import React, { useState, useEffect } from "react";
import { get, post } from "../utils/api";
import KpiCard from "../components/Dashboard/KpiCard";
import LineChart from "../components/Charts/LineChart";
import DonutChart from "../components/Charts/DonutChart";
import BarChart from "../components/Charts/BarChart";
import ProgressBar from "../components/Dashboard/ProgressBar";
import CustomDialog from "../components/dialog/CustomDialog";

// ── Lucide icons ────────────────────────────────────────────────────────────
import {
    TrendingUp,
    Wallet,
    Briefcase,
    Clock,
    Hexagon,
    ArrowUpRight,
    ClipboardList,
    CheckCircle2,
    MapPin
} from "lucide-react";

// ── Components ──────────────────────────────────────────────────────────────

const ProductPerformanceCard = ({ rank, name, arn, clients, aum, commission, status }) => (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-lg transition-all duration-300 group">
        <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-sm">
                    #{rank}
                </div>
                
            </div>
            <div className="text-xs font-semibold text-gray-400">{clients} clients</div>
        </div>

        <div className="mb-4">
            <h4 className="font-bold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{name} - <span>{arn}</span></h4>
        </div>

        <div className="flex justify-between items-end pt-3 border-t border-gray-50">
            <div>
                <p className="text-[10px] text-gray-400 font-medium uppercase mb-0.5">AUM</p>
                <p className="text-[10px] text-gray-400 font-medium uppercase mb-0.5">Commission</p>
            </div>
            <div className="text-right">
                <p className="text-sm font-bold text-gray-900">₹{aum}</p>
                <p className="text-sm font-bold text-green-600">₹{commission}</p>
            </div>
        </div>
    </div>
);

// ── Constants / Sample Data ──────────────────────────────────────────────────

const COMMISSION_TREND_DATA = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
        {
            label: "Upfront",
            data: [15000, 18000, 17000, 22000, 19000, 25000],
            borderColor: "#10B981",
            backgroundColor: "rgba(16,185,129,0.1)",
            fill: true,
            tension: 0.4,
        },
        {
            label: "Trail",
            data: [25000, 28000, 26000, 32000, 30000, 38000],
            borderColor: "#3B82F6",
            backgroundColor: "rgba(59,130,246,0.05)",
            fill: true,
            tension: 0.4,
        },
        {
            label: "Placement",
            data: [35000, 38000, 36000, 45000, 42000, 52000],
            borderColor: "#F59E0B",
            backgroundColor: "rgba(245,158,11,0.05)",
            fill: true,
            tension: 0.4,
        }
    ]
};

const PRODUCT_MAPPING_DATA = {
    labels: ["AIF CAT II", "AIF CAT III", "PMS"],
    data: [12, 8, 10],
    colors: ["#6366F1", "#3B82F6", "#EC4899"],
    legendItems: [
        { label: "AIF CAT II", value: "12", color: "#6366F1" },
        { label: "AIF CAT III", value: "8", color: "#3B82F6" },
        { label: "PMS", value: "10", color: "#EC4899" },
    ],
};

const AUM_GROWTH_DATA = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
        {
            label: "AUM Growth",
            data: [120, 142, 138, 165, 178, 195],
            borderColor: "#6366F1",
            backgroundColor: "#6366F1",
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: "#fff",
            pointBorderColor: "#6366F1",
            pointBorderWidth: 2,
            tension: 0.2,
        }
    ]
};


const DistributorOverviewDashboard = () => {
    const [kpiData, setKpiData] = useState({
        totalAUM: "...",
        mtdCommission: "...",
        productMappings: "...",
        pendingItems: "..."
    });

    const [kpiSubtitles, setKpiSubtitles] = useState({
        acrossClients: "Loading...",
        ytdCommission: "Loading...",
        acrossFunds: "Loading...",
        wipCount: "Loading..."
    });

    const [feeSummaryData, setFeeSummaryData] = useState({
        labels: [],
        datasets: []
    });

    const [productMappingData, setProductMappingData] = useState({
        labels: [],
        data: [],
        colors: [],
        legendItems: [],
        total: "0"
    });

    const [aumTrendData, setAumTrendData] = useState({
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Current"],
        datasets: [
            {
                label: "AUM Growth",
                data: [120, 142, 138, 165, 178, 0],
                borderColor: "#6366F1",
                backgroundColor: "#6366F1",
                borderWidth: 2,
                pointRadius: 4,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#6366F1",
                pointBorderWidth: 2,
                tension: 0.2,
            }
        ]
    });

    const [transactionStatus, setTransactionStatus] = useState({
        completed: 0,
        pending: 0,
        total: 0
    });

    const [topProducts, setTopProducts] = useState([]);
    const [lastUpdated, setLastUpdated] = useState("...");
    const [showLocationDialog, setShowLocationDialog] = useState(false);
    const [locationMessage, setLocationMessage] = useState("");
    const [locationMessageType, setLocationMessageType] = useState("info");

    const userData = JSON.parse(localStorage.getItem("user_details") || "{}");
    const distributorPan = userData.pan_number;

    useEffect(() => {
        const hasSeenLocationPrompt = sessionStorage.getItem("distributor_location_prompt_shown");
        if (!hasSeenLocationPrompt) {
            setShowLocationDialog(true);
            sessionStorage.setItem("distributor_location_prompt_shown", "true");
        }
    }, []);

    const getLocationErrorMessage = (error) => {
        if (!window.isSecureContext) {
            return "Location access is blocked because this site is running on an insecure HTTP connection. On UAT, open this application over HTTPS to enable browser geolocation.";
        }

        if (!error) {
            return "Location access is still blocked. Please allow location access for this site and try again.";
        }

        switch (error.code) {
            case error.PERMISSION_DENIED:
                return "Location permission was denied. Please allow location access in browser site settings and try again.";
            case error.POSITION_UNAVAILABLE:
                return "Your device location is currently unavailable. Please check device location services and try again.";
            case error.TIMEOUT:
                return "Location request timed out. Please try again.";
            default:
                return "Location access is still blocked. Please allow location access for this site and try again.";
        }
    };

    const handleEnableLocationAccess = async () => {
        if (!navigator.geolocation) {
            setLocationMessageType("error");
            setLocationMessage("Location access is not supported in this browser. Please switch to a supported browser and try again.");
            return;
        }

        if (!window.isSecureContext) {
            setLocationMessageType("error");
            setLocationMessage(
                "Location access is blocked because this site is running on an insecure HTTP connection. This UAT URL must be opened over HTTPS. Browser permission Allow alone will not fix geolocation on a Not secure page."
            );
            return;
        }

        if (navigator.permissions?.query) {
            try {
                const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
                if (permissionStatus.state === "denied") {
                    setLocationMessageType("error");
                    setLocationMessage("Location permission is denied in browser site settings. Please change Location to Allow for this site and try again.");
                    return;
                }
            } catch (permissionError) {
                console.warn("Unable to inspect geolocation permission state:", permissionError);
            }
        }

        setLocationMessageType("info");
        setLocationMessage("Waiting for browser permission. Please click Allow when the location access prompt appears.");

        navigator.geolocation.getCurrentPosition(
            () => {
                setLocationMessageType("success");
                setLocationMessage("Location access enabled successfully.");
                setTimeout(() => {
                    setShowLocationDialog(false);
                }, 500);
            },
            (error) => {
                setLocationMessageType("error");
                setLocationMessage(getLocationErrorMessage(error));
            }
        );
    };

    const formatTimestamp = () => {
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const formattedTime = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return `${formattedDate} – ${formattedTime}`;
    };

    useEffect(() => {
        setLastUpdated(formatTimestamp());
        const fetchKpiData = async () => {
            try {
                const [
                    aumRes,
                    commRes,
                    mappingRes,
                    pendingRes,
                    feeSummaryRes,
                    productMappingRes,
                    invoiceStatusRes,
                    productSalesRes,
                    acrossClientRes,
                    wipRes,
                    mtdCommRes,
                    aumTrendRes
                ] = await Promise.all([
                    post("/admindashboard/Get_CurrentMonth_CapitalCommitment_Total", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_FinancialYear_TotalCommission", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_Product_Mapping_Count", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_UTR_Not_Received_Count", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_Fee_Summary_Datewise", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_ProductWise_CapitalCommitment_Total", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_Invoice_Status_Count", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_Top_Products_distributorwise", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_CurrentMonth_Across_Client", { distributor_pan: distributorPan }),
                    post("/admindashboard/GetTotal_WIP_NO_Count", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_Mtd_TotalCommission", { distributor_pan: distributorPan }),
                    post("/admindashboard/Get_AUM_Trend_MonthWise", { distributor_pan: distributorPan }),
                ]);

                setKpiData({
                    totalAUM: aumRes.data.Success ? `₹${(parseFloat(aumRes.data.TotalCapitalCommitement || 0) / 10000000).toFixed(1)} Cr` : "N/A",
                    mtdCommission: mtdCommRes.data.Success ? `₹${(parseFloat(mtdCommRes.data.TotalCommission || 0)).toLocaleString("en-IN")}` : "N/A",
                    productMappings: mappingRes.data.Success && mappingRes.data.Data.length > 0 ? (mappingRes.data.Data[0].count || mappingRes.data.Data[0].total_product_mapping_count || 0) : "N/A",
                    pendingItems: pendingRes.data.Success && pendingRes.data.Data.length > 0 ? (pendingRes.data.Data[0].missing_utr_count || 0) : "N/A"
                });

                if (feeSummaryRes.data.Success && Array.isArray(feeSummaryRes.data.Data)) {
                    const data = feeSummaryRes.data.Data;
                    const labels = data.map(item => {
                        const date = new Date(item.transaction_date);
                        return date.toLocaleDateString("en-IN", { day: '2-digit', month: 'short' });
                    });

                    setFeeSummaryData({
                        labels,
                        datasets: [
                            {
                                label: "Upfront Fee",
                                data: data.map(item => parseFloat(item.total_upfront_fee) || 0),
                                backgroundColor: "#10B981",
                            },
                            {
                                label: "Trail Fee",
                                data: data.map(item => parseFloat(item.total_trail_fee) || 0),
                                backgroundColor: "#3B82F6",
                            },
                            {
                                label: "Placement Fee",
                                data: data.map(item => parseFloat(item.total_placement_fee) || 0),
                                backgroundColor: "#F59E0B",
                            }
                        ]
                    });
                }

                if (productMappingRes.data.Success && Array.isArray(productMappingRes.data.Data)) {
                    const data = productMappingRes.data.Data;

                    // Predefined labels to be displayed
                    const requiredLabels = ["AIF CAT II", "AIF CAT III", "PMS"];
                    const counts = {
                        "AIF CAT II": 0,
                        "AIF CAT III": 0,
                        "PMS": 0
                    };

                    data.forEach(item => {
                        const pName = (item.product_name || "").toUpperCase();
                        const ptName = (item.product_type_name || "").toUpperCase();

                        if (pName === "AIF" && ptName === "CAT II") {
                            counts["AIF CAT II"]++;
                        } else if (pName === "AIF" && ptName === "CAT III") {
                            counts["AIF CAT III"]++;
                        } else if (ptName === "PMS" || pName === "PMS") {
                            counts["PMS"]++;
                        }
                    });

                    const labels = requiredLabels;
                    const values = labels.map(label => counts[label]);
                    const total = values.reduce((a, b) => a + b, 0);
                    const colors = ["#6366F1", "#3B82F6", "#EC4899"];

                    setProductMappingData({
                        labels,
                        data: values,
                        colors: colors,
                        legendItems: labels.map((label, i) => ({
                            label,
                            value: values[i].toString(),
                            color: colors[i]
                        })),
                        total: total.toString()
                    });
                }

                // AUM Growth Trend - Dynamic from API
                if (aumTrendRes.data.Success && Array.isArray(aumTrendRes.data.Data)) {
                    const trendData = aumTrendRes.data.Data;
                    setAumTrendData({
                        labels: trendData.map(item => item.label),
                        datasets: [
                            {
                                label: "AUM Growth",
                                data: trendData.map(item => parseFloat(parseFloat(item.value || 0).toFixed(1))),
                                borderColor: "#6366F1",
                                backgroundColor: "#6366F1",
                                borderWidth: 2,
                                pointRadius: 4,
                                pointBackgroundColor: "#fff",
                                pointBorderColor: "#6366F1",
                                pointBorderWidth: 2,
                                tension: 0.2,
                            }
                        ]
                    });
                }

                // Transaction Status
                if (invoiceStatusRes.data.Success && invoiceStatusRes.data.Data.length > 0) {
                    const statusData = invoiceStatusRes.data.Data[0];
                    const approved = parseInt(statusData.APPROVED || 0);
                    const pending = parseInt(statusData.PENDING_FOR_APPROVAL || 0);
                    setTransactionStatus({
                        completed: approved,
                        pending: pending,
                        total: approved + pending
                    });
                }

                // Top Products
                if (productSalesRes.data.Success && Array.isArray(productSalesRes.data.Data)) {
                    setTopProducts(productSalesRes.data.Data.slice(0, 4));
                }

                // ── Dynamic KPI Subtitles ────────────────────────────────────
                const newSubtitles = { ...kpiSubtitles };

                // Card 1: Across X distributor
                if (acrossClientRes.data.Success) {
                    const clientCount = acrossClientRes.data.TotalDistributors || 0;
                    newSubtitles.acrossClients = `Across ${clientCount} distributor${clientCount !== 1 ? 's' : ''}`;
                }

                // Card 2: YTD total commission
                if (commRes.data.Success) {
                    const ytd = parseFloat(commRes.data.TotalCommission || 0);
                    const fy = commRes.data.FinancialYear || "";
                    newSubtitles.ytdCommission = `YTD ${fy}: ₹${ytd.toLocaleString("en-IN")}`;
                }

                // Card 3: Across X funds
                if (mappingRes.data.Success && mappingRes.data.Data.length > 0) {
                    const fundCount = mappingRes.data.Data[0].count || mappingRes.data.Data[0].total_product_mapping_count || 0;
                    newSubtitles.acrossFunds = `Across funds`;
                }

                // Card 4: WIP distributor count
                if (wipRes.data.Success && wipRes.data.Data.length > 0) {
                    const wipCount = wipRes.data.Data[0].total_wip_no_count || 0;
                    newSubtitles.wipCount = `${wipCount} WIP distributor${wipCount !== 1 ? 's' : ''}`;
                }

                setKpiSubtitles(newSubtitles);
                setLastUpdated(formatTimestamp());
            } catch (error) {
                console.error("Error fetching KPI data:", error);
                setKpiData({
                    totalAUM: "N/A",
                    mtdCommission: "N/A",
                    productMappings: "N/A",
                    pendingItems: "N/A"
                });
            }
        };
        fetchKpiData();
    }, []);


    return (
        <div className="distributor-overview p-6 space-y-6 max-w-[1600px] mx-auto bg-[#F9FBFC] min-h-screen">
            <CustomDialog
                visible={showLocationDialog}
                onHide={() => setShowLocationDialog(false)}
                header="Enable Browser Permissions"
                width="560px"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-full bg-indigo-50 p-2 text-indigo-600">
                            <MapPin className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900">
                                Please enable location access after distributor login.
                            </p>
                            <p className="mt-1 text-sm text-gray-600">
                                Browser geolocation works only on secure origins such as HTTPS or localhost. If this page shows <strong>Not secure</strong>, location access can fail even if browser permission is set to Allow.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Steps</p>
                        <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm text-gray-700">
                            <li>Click <strong>Enable Location Access</strong> below.</li>
                            <li>When the browser prompt appears, click <strong>Allow</strong>.</li>
                            <li>If the site is running on UAT and the address bar shows <strong>Not secure</strong>, switch the site to <strong>HTTPS</strong>. HTTP IP-based URLs can block geolocation.</li>
                            <li>If HTTPS is already enabled, open site settings in the browser and make sure <strong>Location</strong> is set to <strong>Allow</strong>.</li>
                        </ol>
                    </div>

                    {locationMessage ? (
                        <div
                            className={`rounded-xl px-4 py-3 text-sm ${
                                locationMessageType === "error"
                                    ? "border border-red-100 bg-red-50 text-red-700"
                                    : locationMessageType === "success"
                                        ? "border border-emerald-100 bg-emerald-50 text-emerald-700"
                                        : "border border-indigo-100 bg-indigo-50 text-indigo-700"
                            }`}
                        >
                            {locationMessage}
                        </div>
                    ) : null}

                    <div className="flex justify-end gap-3 pt-1">
                        <button
                            type="button"
                            onClick={() => setShowLocationDialog(false)}
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            onClick={handleEnableLocationAccess}
                            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                        >
                            Enable Location Access
                        </button>
                    </div>
                </div>
            </CustomDialog>

            {/* Header */}
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 tracking-tight">Distributor Commission Summary</h1>
                    <p className="text-gray-500 text-sm font-medium">Manage your products, transactions, and commission earnings.</p>
                </div>
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest pb-1">
                    Last updated: <span className="text-gray-900">{lastUpdated}</span>
                </span>
            </div>

            {/* KPI Cards */}
            <div className="admin-kpi-grid">
                <KpiCard
                    title="Total AUM"
                    value={kpiData.totalAUM}
                    subtitle={kpiSubtitles.acrossClients}
                    trend={15}
                    icon={<TrendingUp size={22} strokeWidth={2.5} />}
                    iconBgColor="#6366F1"
                    iconColor="#fff"
                />
                <KpiCard
                    title="Commission (MTD)"
                    value={kpiData.mtdCommission}
                    subtitle={kpiSubtitles.ytdCommission}
                    trend={22}
                    icon={<Wallet size={22} strokeWidth={2.5} />}
                    iconBgColor="#10B981"
                    iconColor="#fff"
                />
                <KpiCard
                    title="Product Mappings"
                    value={kpiData.productMappings}
                    subtitle={kpiSubtitles.acrossFunds}
                    trend={10}
                    icon={<CheckCircle2 size={22} strokeWidth={2.5} />}
                    iconBgColor="#8B5CF6"
                    iconColor="#fff"
                />
                <KpiCard
                    title="Pending Items"
                    value={kpiData.pendingItems}
                    subtitle={kpiSubtitles.wipCount}
                    trend={-5}
                    icon={<Clock size={22} strokeWidth={2.5} />}
                    iconBgColor="#F59E0B"
                    iconColor="#fff"
                />
            </div>

            {/* Row 1: Fee Summary + Product Mappings */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 dash-card">
                    <div className="dash-card-header mb-6">
                        <h3 className="dash-section-title">Fee Summary</h3>
                        <p className="dash-section-sub">Daily commission breakdown</p>
                    </div>
                    <div className="overflow-x-auto pb-2">
                        <div style={{
                            minWidth: feeSummaryData.labels.length > 10 ? `${feeSummaryData.labels.length * 60}px` : '100%'
                        }}>
                            <BarChart
                                labels={feeSummaryData.labels}
                                datasets={feeSummaryData.datasets}
                                height={300}
                                showLegend={true}
                            />
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-2 dash-card">
                    <div className="dash-card-header mb-6">
                        <h3 className="dash-section-title">Product Mappings</h3>
                        <p className="dash-section-sub">By product type</p>
                    </div>
                    <DonutChart
                        labels={productMappingData.labels}
                        data={productMappingData.data}
                        colors={productMappingData.colors}
                        centerLabel={productMappingData.total}
                        height={220}
                        legendItems={productMappingData.legendItems}
                    />
                </div>
            </div>

            {/* Row 2: AUM Growth Trend + Transaction Status */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 dash-card">
                    <div className="dash-card-header mb-6">
                        <h3 className="dash-section-title">AUM Growth Trend</h3>
                        <p className="dash-section-sub">Asset Under Management (in Crores)</p>
                    </div>
                    <LineChart
                        labels={aumTrendData.labels}
                        datasets={aumTrendData.datasets}
                        height={250}
                    />
                </div>
                <div className="dash-card">
                    <div className="dash-card-header mb-6">
                        <h3 className="dash-section-title">Transaction Status</h3>
                        <p className="dash-section-sub">Total {transactionStatus.total} transactions</p>
                    </div>
                    <div className="mt-8">
                        <ProgressBar label="Completed" value={transactionStatus.completed} total={transactionStatus.total} color="#10B981" />
                        <ProgressBar label="Pending" value={transactionStatus.pending} total={transactionStatus.total} color="#F59E0B" />
                        <ProgressBar label="Processing" value={0} total={transactionStatus.total} color="#3B82F6" />
                    </div>
                </div>
            </div>

            {/* Row 3: Top Performing Products */}
            <div className="dash-card !bg-transparent !p-0 !border-none !shadow-none">
                <div className="dash-card-header mb-4">
                    <h3 className="dash-section-title">Top Performing Products</h3>
                    <p className="dash-section-sub">Your best revenue generators</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {topProducts.length > 0 ? topProducts.map((product, index) => (
                        <ProductPerformanceCard
                            key={index}
                            rank={index + 1}
                            name={product.fund_code}
                            arn={product.product_code}
                            clients={product.record_count}
                            aum={`${(parseFloat(product.total_commitment_amount || 0) / 10000000).toFixed(1)} Cr`}
                            commission={parseFloat(product.total_commission || 0).toLocaleString("en-IN")}
                            status="Active"
                        />
                    )) : (
                        <div className="col-span-4 p-8 text-center bg-white rounded-2xl border border-dashed border-gray-200 text-gray-400 font-medium">
                            No product performance data available
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default DistributorOverviewDashboard;
